import React, { useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Alert } from "@mui/material";
import AdminLayout from "../AdminLayout";
import { useAuth } from "../../hooks/useAuth";
import ev from "../../services/eventV2Service";
import { validateEventPayload } from "../../utils/validation";
import { validateQuizJSON } from "../../utils/quizSchema";

export default function AdminEventV2Create() {
  const { userProfile } = useAuth();
  const [type, setType] = useState("internal");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [speakers, setSpeakers] = useState("");
  const [externalRegistrationLink, setExternalRegistrationLink] = useState("");
  const [creditCap, setCreditCap] = useState(0);
  const [cutoff, setCutoff] = useState(80);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [creating, setCreating] = useState(false);
  const [quizJsonText, setQuizJsonText] = useState('{\n  "questions": [\n    {\n      "question": "Eligibility criteria?",\n      "options": ["Yes","No","Depends"],\n      "correctAnswer": "Yes",\n      "credit": 20\n    }\n  ]\n}');
  const [quizError, setQuizError] = useState("");

  const DEFAULT_TIMELINE_INTERNAL = [
    { key: "registration", name: "Registration Open", status: "pending" },
    { key: "attendance", name: "Attendance Tracking", status: "pending" },
    { key: "feedback", name: "Feedback Collection", status: "pending" },
    { key: "certificates", name: "Certificate Distribution", status: "pending" }
  ];
  const DEFAULT_TIMELINE_EXTERNAL = [
    { key: "registration", name: "Registration Link", status: "pending" },
    { key: "confirmation", name: "Confirmation Received", status: "pending" },
    { key: "approval", name: "Approval from Authority", status: "pending" },
    { key: "travel", name: "Travel Arranged", status: "pending" },
    { key: "attendance", name: "Event Attended", status: "pending" },
    { key: "outcome", name: "Outcome/Result", status: "pending" },
    { key: "return", name: "Return to Organization", status: "pending" },
    { key: "expenses", name: "Expense Submission", status: "pending" },
    { key: "reimburse", name: "Reimbursement Process", status: "pending" },
    { key: "closure", name: "Closure", status: "pending" }
  ];

  const handleCreate = async () => {
    setError("");
    setSuccess("");
    try {
      let quiz = null;
      if (type === "external") {
        setQuizError("");
        try {
          const obj = JSON.parse(quizJsonText);
          const res = validateQuizJSON(obj);
          if (!res.valid) {
            setQuizError(res.error);
            setError("Fix quiz JSON: " + res.error);
            return;
          }
          quiz = obj;
        } catch (e) {
          setQuizError("Invalid JSON: " + e.message);
          setError("Invalid quiz JSON");
          return;
        }
      }
      const payload = {
        type,
        title,
        description,
        date,
        venue,
        speakers: speakers ? speakers.split(",").map(s => s.trim()) : [],
        externalRegistrationLink: type === "external" ? externalRegistrationLink.trim() : "",
        credit_cap: Number(creditCap || 0),
        cutoff: type === "external" ? Number(cutoff || 0) : 0,
        timeline: type === "internal"
          ? DEFAULT_TIMELINE_INTERNAL
          : DEFAULT_TIMELINE_EXTERNAL.map((st) => st.key === "registration" ? { ...st, data: { registrationLink: externalRegistrationLink.trim() } } : st),
        quiz
      };
      if (type === "external" && !externalRegistrationLink.trim()) {
        setError("Registration link is required for external events");
        return;
      }
      if (type === "external") {
        try {
          new URL(externalRegistrationLink.trim());
        } catch {
          setError("Enter a valid external registration link");
          return;
        }
      }
      const v = validateEventPayload(payload);
      if (!v.valid) {
        setError(v.errors.join("; "));
        return;
      }
      setCreating(true);
      const evtId = await ev.createEvent(userProfile?.id || userProfile?.uid || "ADMIN", payload);
      setSuccess("Event created");
      setTitle("");
      setDescription("");
      setDate("");
      setVenue("");
      setSpeakers("");
      setExternalRegistrationLink("");
      setCreditCap(0);
      setCutoff(80);
      setQuizJsonText('{\n  "questions": []\n}');
    } catch (e) {
      setError(e.message || "Failed to create event");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminLayout title="Create Event">
      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Card sx={{ borderTop: "6px solid #003366" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>Create Event</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" label="Venue" value={venue} onChange={(e) => setVenue(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Speakers (comma-separated)" value={speakers} onChange={(e) => setSpeakers(e.target.value)} sx={{ mb: 2 }} />
            {type === "external" && (
              <TextField
                fullWidth
                size="small"
                label="External Registration Link"
                value={externalRegistrationLink}
                onChange={(e) => setExternalRegistrationLink(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
            <TextField fullWidth size="small" label="Credit Cap" type="number" value={creditCap} onChange={(e) => setCreditCap(e.target.value)} sx={{ mb: 2 }} />
            {type === "external" && (
              <>
                <TextField fullWidth size="small" label="Cutoff (%)" type="number" value={cutoff} onChange={(e) => setCutoff(e.target.value)} sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#003366", mt: 1, mb: 1 }}>
                  Quiz JSON (per-question 3–5 options, correctAnswer, credit)
                </Typography>
                {quizError && <Alert severity="error" sx={{ mb: 2 }}>{quizError}</Alert>}
                <TextField
                  fullWidth
                  multiline
                  minRows={12}
                  value={quizJsonText}
                  onChange={(e) => setQuizJsonText(e.target.value)}
                  placeholder='{"questions":[{"question":"...","options":["A","B","C"],"correctAnswer":"B","credit":20}]}'
                  sx={{ mb: 2 }}
                />
              </>
            )}
            <Button variant="contained" disabled={creating} onClick={handleCreate}>Create Event</Button>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
